import { SubmitEvent, useState } from 'react';
import axios from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { AlertCircle } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { OAuthButtons } from '@/features/oauthSso';
import { useAuth } from '@/hooks/useAuth';
import { useSessionExpired } from '@/hooks/useSessionExpired';
import { abandonSession, resumeAfterReauth } from '@/lib/sessionExpiry';
import { User } from '@/hooks/useUser';
import { config } from '@/config';
import { SecondFactorPrompt } from '@/features/auth/SecondFactorPrompt';
import { MfaChallenge, isMfaChallenge } from '@/features/auth/mfaChallenge';
import { useHeldAccounts } from '@/hooks/useHeldAccounts';
import { switchAccount } from '@/lib/activeAccount';

/**
 * Where to come back to after re-authenticating through a flow that leaves the page (SSO).
 *
 * Not used by the sign-out path: coming back is the opposite of what that button is for.
 */
function currentPath(): string {
  return `${window.location.pathname}${window.location.search}`;
}

/**
 * Blocking re-auth prompt shown when the session cookie lapses mid-use.
 *
 * The point of doing this in a dialog rather than redirecting to /signin is
 * that nothing unmounts: the page underneath keeps its state, half-filled
 * forms included, and the requests that hit the 401 are replayed on the way
 * out (see `lib/sessionExpiry`). The user lands back exactly where they were.
 *
 * The email is fixed to the cached user. Signing in as somebody else would
 * leave the page rendering the previous account's data and replay their
 * requests under new credentials, so that path deliberately goes through a
 * full sign-out instead.
 */
export function SessionExpiredDialog() {
  const expired = useSessionExpired();
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [password, setPassword] = useState('');
  const [err, setErr] = useState<string | undefined>(undefined);
  const [submitting, setSubmitting] = useState(false);
  const [challenge, setChallenge] = useState<MfaChallenge | undefined>(undefined);
  // The browser's other signed-in accounts, which this tab can carry on as instead
  // (docs/MultiAccountSessions.md §5).
  const { data: held = [] } = useHeldAccounts(expired && !!user);

  // Nothing to re-authenticate against without a cached identity; the route
  // guards own the signed-out case.
  if (!expired || !user) {
    return null;
  }
  const otherAccounts = held.filter((account) => account.signedIn && account.id !== user.id);

  /**
   * Actually sign out, via `/signout`.
   *
   * This used to navigate straight to `/signin?returnTo=<here>` without calling logout at
   * all, on the reasoning that the session is dead by the time this dialog exists, so
   * there is nothing server-side left to end. Two things make that wrong. The premise is
   * a proxy rather than proof — `markSessionExpired` fires on any non-auth 401 while a
   * user is cached, so the cookie may well still be good — and if it is, `/signin` sees a
   * live session and bounces straight back to `returnTo`, returning the user to the page
   * they were trying to leave, where the same 401 raises this dialog again.
   *
   * It is also no longer true that logout has nothing to do: it revokes the session row
   * (`docs/AuthSessions.md` §7), so skipping it leaves a live session the user believes
   * they ended.
   *
   * `/signout` handles the expired case correctly — it treats a 401 from logout as
   * success — and lands on `/signin?signedOut=1` with no `returnTo`, so there is no bounce
   * back.
   *
   * A router push, not a page load. `/signout` ends on a full navigation to `/signin`, and
   * that one is what drops every piece of cached state — doing it here as well would make
   * one sign-out cost two page loads to reach the same place. `abandonSession()` settles
   * anything parked and clears the flag, so this dialog unmounts on the spot.
   */
  const signOut = () => {
    abandonSession();
    void navigate('/signout');
  };

  /**
   * Finish a re-authentication, however the factors were supplied.
   *
   * Re-reads the identity before replaying anything, so a session that somehow came back as a
   * different user tears down instead of silently reusing the previous account's page.
   */
  const settleReauth = async () => {
    const { data: refreshed } = await axios.get<User>(`${config.backendApi}/api/auth/current`, {
      withCredentials: true,
    });
    if (refreshed.id !== user.id) {
      signOut();
      return;
    }

    queryClient.setQueryData(['currentUser'], refreshed);
    setPassword('');
    setChallenge(undefined);
    setSubmitting(false);
    await resumeAfterReauth();
  };

  const onSubmit = (e: SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErr(undefined);
    setSubmitting(true);

    void axios
      .post(`${config.backendApi}/api/auth/login`, { email: user.email, password }, { withCredentials: true })
      .then(
        async (res) => {
          // ⚠ **A 200 is not necessarily a session.** A user holding a second factor gets
          // `mfaRequired` and no cookie; falling through would re-read the *old* identity and
          // report "incorrect password" for a password that was in fact correct.
          if (isMfaChallenge(res.data)) {
            setChallenge(res.data);
            setSubmitting(false);
            return;
          }
          await settleReauth();
        },
        () => {
          setErr('Incorrect password. Try again, or sign out to start over.');
          setSubmitting(false);
        },
      );
  };

  return (
    <AlertDialog open>
      <AlertDialogContent
        className="max-w-sm"
        onEscapeKeyDown={(e) => { e.preventDefault(); }}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>Your session expired</AlertDialogTitle>
          <AlertDialogDescription>
            Sign in again to pick up where you left off. Nothing on this page has been lost.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {err ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{err}</AlertDescription>
          </Alert>
        ) : null}

        {challenge ? (
          // The password was accepted; only the second factor is outstanding. `settleReauth`
          // rather than the default invalidate, so the identity re-check and the parked-request
          // replay still happen on this path.
          <SecondFactorPrompt
            challenge={challenge}
            onCancel={() => { setChallenge(undefined); setPassword(''); }}
            onAuthenticated={settleReauth}
          />
        ) : null}

        {!challenge && user.hasPassword ? (
          <form onSubmit={onSubmit} className="flex flex-col gap-4">
            <div className="grid gap-2">
              <Label htmlFor="session-expired-email">Email</Label>
              <Input id="session-expired-email" type="email" value={user.email} readOnly disabled />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="session-expired-password">Password</Label>
              <Input
                id="session-expired-password"
                type="password"
                autoComplete="current-password"
                autoFocus
                value={password}
                onChange={(e) => { setPassword(e.target.value); }}
                required
              />
            </div>
            <Button type="submit" className="w-full" disabled={submitting || password.length === 0}>
              {submitting ? 'Signing in…' : 'Sign in and continue'}
            </Button>
          </form>
        ) : null}

        {/* SSO is a top-level redirect, so this one *does* leave the page —
            `returnTo` brings them back to the same URL, but unsaved edits on
            it will not survive. Said plainly rather than hidden.

            Scoped to the cached user's org, not just the globals: someone who
            signed in through their org's own provider has no password (SSO
            sign-up leaves it empty), so without their provider listed here
            there is nothing to re-authenticate with. */}
        <div className="flex flex-col gap-2">
          <OAuthButtons
            organizationId={user.organizationId}
            returnTo={currentPath()}
            withDivider={user.hasPassword}
          />
        </div>

        {otherAccounts.length > 0 ? (
          // Another account picks up at home: this page may belong to an organization it can't
          // reach. A full load, so nothing of this account's page carries across.
          <div className="flex flex-col gap-2">
            <p className="text-xs text-muted-foreground text-center">Or continue as</p>
            {otherAccounts.map((account) => (
              <Button
                key={account.id}
                variant="outline"
                className="w-full truncate"
                onClick={() => {
                  abandonSession();
                  switchAccount(account.id, '/home');
                }}
              >
                {account.email}
              </Button>
            ))}
          </div>
        ) : null}

        <div className="pt-1 text-center">
          <button
            type="button"
            className="text-xs text-muted-foreground underline underline-offset-4 hover:text-foreground"
            onClick={signOut}
          >
            Sign out instead
          </button>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
}
